<template>
  <div class="stack-content">
    <common-card title="Stacks Search" class="groups">
      <search-form
        :search-form="searchForm"
        :search-form-change="searchFormChange"
      />
      <el-alert
        v-if="stackSlots.length === 0"
        class="search-hint"
        title="Nothing found!"
        type="warning"
        :closable="false"
      />
    </common-card>
    <div class="f-r two-tables" v-if="stackSlots.length > 0">
      <common-card title="Stacks List" class="groups group-left">
        <div class="list">
          <basic-table
            class="slot-list"
            ref="slotList"
            :table-data="stackSlots"
            :table-headers="slotTableHeaders"
            :hightlight="true"
            @selectedRow="selectedRowChange"
          />
        </div>
      </common-card>
      <common-card class="groups group-right">
        <div class="list">
          <el-tabs v-model="selectedTab" @tab-click="selectTabChange">
            <el-tab-pane label="Stack Info">
              <el-alert
                v-if="!selectedRow"
                class="search-hint"
                title="Please click a row!"
                type="warning"
                :closable="false"
              />
              <stack-info
                v-if="selectedRow"
                :row-no="rowNo"
                :stack-no="stackNo"
                :section-data="sectionData"
                :stack-section-table-headers="stackSectionTableHeaders"
              />
            </el-tab-pane>
            <el-tab-pane label="Section Info">
              <el-alert
                v-if="!selectedRow"
                class="search-hint"
                title="Please click a row!"
                type="warning"
                :closable="false"
              />
              <stack-section
                v-if="selectedRow"
                :row-no="rowNo"
                :stack-no="stackNo"
                :section-no="sectionNo"
                :section-slot-data="sectionSlotData"
                :section-slot-table-headers="sectionSlotTableHeaders"
              />
            </el-tab-pane>
            <el-tab-pane label="Pallet List">
              <el-alert
                v-if="!selectedRow"
                class="search-hint"
                title="Please click a row!"
                type="warning"
                :closable="false"
              />
              <pallet-list :pallets="pallets" v-if="selectedRow">
                <template v-slot:actions="props">
                  <el-button
                    size="small"
                    type="text"
                    @click="goToPalletDetail(props.row.no)"
                  >
                    Detail
                  </el-button>
                </template>
              </pallet-list>
            </el-tab-pane>
          </el-tabs>
        </div>
      </common-card>
    </div>
  </div>
</template>

<script>
// components
import CommonCard from '@/components/common/CommonCard.vue'
import SearchForm from '@/components/common/SearchForm.vue'
import BasicTable from '@/components/common/BasicTable.vue'
import StackInfo from '@/components/stack/StackInfo.vue'
import StackSection from '@/components/stack/StackSection.vue'
import PalletList from '@/components/pallet/PalletList.vue'
// api
import { parseStackNo, filterSlot, calcLoadRate } from '@/api/stack'
// mixin
import { pageJumps } from '@/core/mixin/pages'

export default {
  name: 'StackContent',
  props: {
    stacks: {
      type: Object,
      required: true
    },
    searchForm: {
      type: Object,
      required: true
    },
    searchFormChange: {
      type: Function,
      required: true
    },
    stackSlots: {
      type: Array,
      default: () => []
    },
    slotTableHeaders: {
      type: Array,
      required: true
    },
    stackSectionTableHeaders: {
      type: Array,
      required: true
    },
    sectionSlotTableHeaders: {
      type: Array,
      required: true
    }
  },
  mixins: [pageJumps],
  data() {
    return {
      selectedTab: '',
      selectedRow: null,
      sectionData: [],
      sectionSlotData: [],
      pallets: []
    }
  },
  computed: {
    rowNo() {
      return this.selectedRow?.rowNo
    },
    stackNo() {
      return this.selectedRow?.stackNo
    },
    sectionNo() {
      return this.selectedRow?.sectionNo
    }
  },
  methods: {
    selectTabChange() {},
    setCurrent(row) {
      this.$refs.slotList.setCurrentRow(row)
    },
    selectedRowChange(row) {
      this.selectedRow = row
      const stack = this.stacks[row.rowNo + row.stackNo]
      this.getSectionData(stack)
      this.getSectionSlotData(stack, row.sectionNo)
      this.getSlotPallets(
        stack,
        row.rowNo + row.stackNo + row.sectionNo + row.slotNo
      )
    },
    getSectionData(stack) {
      const sectionNum = stack.sectionNum
      const sectionData = []
      for (let i = 0; i < sectionNum; i++) {
        const temp = {}
        temp.sectionNo = '0' + (i + 1)
        const filters = { sectionNo: temp.sectionNo }
        const slotsWithTheSectionNo = stack.slots.filter(slot => {
          const parsed = parseStackNo(slot.no)
          return filterSlot(parsed, filters)
        })
        temp.loadRate = calcLoadRate(slotsWithTheSectionNo)
        sectionData.push(temp)
      }
      this.sectionData = sectionData
    },
    getSectionSlotData(stack, sectionNo) {
      const slotPerSection = stack.slotPerSection
      const sectionSlotData = []
      for (let i = 0; i < slotPerSection; i++) {
        const temp = {}
        temp.slotNo = String.fromCharCode(65 + i)
        const slotsWithTheSlotNo = stack.slots.filter(slot => {
          const parsed = parseStackNo(slot.no)
          const filters = { slotNo: temp.slotNo, sectionNo: sectionNo }
          return filterSlot(parsed, filters)
        })
        // temp.palletNo = slotsWithTheSlotNo[0].pallet?.no ?? ''
        temp.palletCnt = (slotsWithTheSlotNo[0].pallets ?? []).length
        temp.id = slotsWithTheSlotNo[0].id
        temp.no = slotsWithTheSlotNo[0].no
        sectionSlotData.push(temp)
      }
      this.sectionSlotData = sectionSlotData
    },
    getSlotPallets(stack, slotNo) {
      const slotList = stack.slots.filter(e => e.no === slotNo)
      const slot = slotList[0]
      this.pallets = slot.pallets
    }
  },
  components: {
    CommonCard,
    SearchForm,
    BasicTable,
    StackInfo,
    StackSection,
    PalletList
  },
  mounted() {}
}
</script>
<style lang="scss" scoped>
@import '@/assets/styles/common.scss';

.stack-content {
  @extend .f-c;

  .search-hint {
    margin-top: 5px;
  }

  .two-tables {
    flex-grow: 1;
    gap: $home-card-padding-x;

    .common-card {
      margin-bottom: $home-card-padding-x;

      .list {
        height: 100%;

        @extend .f-c;
      }
    }

    .slot-list {
      height: 100%;

      .table-container {
        height: 100%;
      }
    }

    .group-left {
      margin-right: 0;
      width: 50%;
      padding-bottom: 15px;
    }

    .group-right {
      margin-left: 0;
      width: 50%;
      padding-bottom: 15px;

      .el-tabs {
        margin-top: -18px;

        ::v-deep .el-tabs__item {
          font-size: 12px;
          font-weight: bold;
        }
      }
    }

    ::v-deep .el-table__row {
      height: 50px !important;

      &:hover {
        cursor: pointer;
      }
    }
  }
}
</style>
